/**
 * Public Compare Plans Page
 * Shareable at /pricing/compare – same comparison data as protected ComparePlansPage, minimal layout.
 * No auth; CTA to sign up or view pricing.
 *
 * @author Varun Pandey
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { GitCompare, Check, X, ChevronDown, ChevronRight, ArrowLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { PublicLayout } from '@/components/layout/PublicLayout';
import {
  COMPARE_PLANS,
  COMPARE_PRICES,
  COMPARE_EMI,
  COMPARE_SECTIONS,
  getCompareCell,
  type BillingPeriod,
  type CellValue,
} from '@/data/comparePlansData';

function Cell({ value }: { value: CellValue }) {
  if (typeof value === 'boolean') {
    return value ? (
      <Check className="h-5 w-5 text-emerald-500 shrink-0" />
    ) : (
      <X className="h-5 w-5 text-[#636366] shrink-0" />
    );
  }
  return (
    <span className="text-[#E5E5EA] text-xs lg:text-sm font-medium text-center">{value || '—'}</span>
  );
}

function modIndex(n: number, length: number) {
  return ((n % length) + length) % length;
}

function getFontFamilyForMeasure() {
  if (typeof document === 'undefined') return 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
  const fam = window.getComputedStyle(document.body).fontFamily;
  return fam || 'Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif';
}

function measureTextPx(text: string, font: string, letterSpacingPx: number) {
  if (!text) return 0;
  if (typeof document === 'undefined') {
    const fontSizeMatch = font.match(/(\d+(?:\.\d+)?)px/);
    const fontSize = fontSizeMatch ? Number(fontSizeMatch[1]) : 12;
    return text.length * fontSize * 0.62 + Math.max(0, text.length - 1) * letterSpacingPx;
  }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return text.length * 7;
  ctx.font = font;
  const base = ctx.measureText(text).width;
  return base + Math.max(0, text.length - 1) * letterSpacingPx;
}

function sliceHeadToFit(text: string, maxPx: number, font: string, letterSpacingPx: number) {
  if (!text) return '';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const sub = text.slice(0, mid);
    if (measureTextPx(sub, font, letterSpacingPx) <= maxPx) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(0, lo);
}

function sliceTailToFit(text: string, maxPx: number, font: string, letterSpacingPx: number) {
  if (!text) return '';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    const sub = text.slice(text.length - mid);
    if (measureTextPx(sub, font, letterSpacingPx) <= maxPx) lo = mid;
    else hi = mid - 1;
  }
  return text.slice(text.length - lo);
}

function QuarterCircleSectionPicker({
  labels,
  selectedIndex,
  onChange,
  forcedShown,
  onForceShow,
}: {
  labels: string[];
  selectedIndex: number;
  onChange: (nextIndex: number) => void;
  forcedShown?: boolean;
  onForceShow?: () => void;
}) {
  const [isShown, setIsShown] = useState(false);
  const lastScrollTopRef = useRef(0);
  const scrollElRef = useRef<HTMLElement | Window | null>(null);

  const r = 200;
  const arcInset = 32;
  const rr = r - arcInset;
  const arcLen = (Math.PI / 2) * rr;
  const gapPx = 10;
  const hintInset = arcInset + 18;
  const rHintClockwise = r - hintInset;
  const rHintAnti = rHintClockwise - 12;

  const arcPaths = useMemo(() => {
    const cx = r;
    const cy = r;
    const polar = (deg: number) => {
      const rad = (deg * Math.PI) / 180;
      return { x: cx + rr * Math.cos(rad), y: cy + rr * Math.sin(rad) };
    };
    const polarHint = (deg: number, radius: number) => {
      const rad = (deg * Math.PI) / 180;
      return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
    };
    const arc = (startDeg: number, endDeg: number) => {
      const s = polar(startDeg);
      const e = polar(endDeg);
      return `M ${s.x} ${s.y} A ${rr} ${rr} 0 0 1 ${e.x} ${e.y}`;
    };
    const arcHint = (startDeg: number, endDeg: number, radius: number) => {
      const s = polarHint(startDeg, radius);
      const e = polarHint(endDeg, radius);
      return `M ${s.x} ${s.y} A ${radius} ${radius} 0 0 1 ${e.x} ${e.y}`;
    };

    const a0 = 180;
    const a3 = 270;
    const a1 = 198;
    const a2 = 242;

    return {
      all: arc(a0, a3),
      prev: arc(a0, a1),
      selected: arc(a1, a2),
      next: arc(a2, a3),
      hintClockwise: arcHint(208, 258, rHintClockwise),
      hintAnti: arcHint(208, 258, rHintAnti),
    };
  }, [rHintAnti, rHintClockwise, rr]);

  const len = labels.length;
  const prevIndex = modIndex(selectedIndex - 1, len);
  const nextIndex = modIndex(selectedIndex + 1, len);

  useEffect(() => {
    const t = window.setTimeout(() => setIsShown(true), 80);
    return () => window.clearTimeout(t);
  }, []);

  useEffect(() => {
    if (typeof forcedShown === 'boolean') return;
    if (typeof document === 'undefined') return;
    const getScrollParent = (): HTMLElement | null => {
      // Prefer explicit page container first.
      const explicit = document.querySelector<HTMLElement>('[data-scroll-container]');
      if (explicit) return explicit;

      const knownApp = document.querySelector<HTMLElement>('.content-safe-scroll');
      if (knownApp) return knownApp;

      const candidates = Array.from(document.querySelectorAll<HTMLElement>('main'));
      for (const el of candidates) {
        const style = window.getComputedStyle(el);
        const oy = style.overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 8) return el;
      }
      return null;
    };

    const scrollEl = getScrollParent();
    if (scrollEl) {
      scrollElRef.current = scrollEl;
      lastScrollTopRef.current = scrollEl.scrollTop;
      const onScroll = () => {
        const current = scrollEl.scrollTop;
        const delta = current - lastScrollTopRef.current;

        // Downward movement hides with a tiny threshold.
        // Any upward movement should reveal immediately (prevents "stuck collapsed" behavior).
        if (delta > 2) setIsShown(false);
        else if (delta < 0) setIsShown(true);

        lastScrollTopRef.current = current;
      };
      scrollEl.addEventListener('scroll', onScroll, { passive: true });
      return () => scrollEl.removeEventListener('scroll', onScroll as EventListener);
    }

    // Fallback to window scroll for public pages.
    scrollElRef.current = window;
    lastScrollTopRef.current = window.scrollY;
    const onWindowScroll = () => {
      const current = window.scrollY;
      const delta = current - lastScrollTopRef.current;
      if (delta > 2) setIsShown(false);
      else if (delta < 0) setIsShown(true);
      lastScrollTopRef.current = current;
    };
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', onWindowScroll);
  }, [forcedShown]);

  const arcTextLayout = useMemo(() => {
    const family = getFontFamilyForMeasure();
    const prevFontSize = 11.5;
    const prevWeight = 600;
    const prevLetterSpacingPx = prevFontSize * 0.06;
    const prevFont = `${prevWeight} ${prevFontSize}px ${family}`;
    const selFontSize = 12.5;
    const selWeight = 800;
    const selLetterSpacingPx = selFontSize * 0.08;
    const selFont = `${selWeight} ${selFontSize}px ${family}`;

    const prevLabel = String(labels[prevIndex] ?? '').toUpperCase();
    const selLabel = String(labels[selectedIndex] ?? '').toUpperCase();
    const nextLabel = String(labels[nextIndex] ?? '').toUpperCase();

    const selPx = measureTextPx(selLabel, selFont, selLetterSpacingPx);
    const safeSelPx = Math.min(selPx, Math.max(arcLen - 2 * gapPx, arcLen * 0.92));
    const remaining = Math.max(0, arcLen - safeSelPx - 2 * gapPx);
    const sideMax = remaining / 2;

    const prevShown = sliceTailToFit(prevLabel, sideMax, prevFont, prevLetterSpacingPx);
    const nextShown = sliceHeadToFit(nextLabel, sideMax, prevFont, prevLetterSpacingPx);

    const centerStartPx = arcLen / 2 - safeSelPx / 2;
    const centerEndPx = arcLen / 2 + safeSelPx / 2;
    const prevCenterPx = Math.max(gapPx, centerStartPx - gapPx - sideMax / 2);
    const nextCenterPx = Math.min(arcLen - gapPx, centerEndPx + gapPx + sideMax / 2);
    const pct = (px: number) => `${(Math.max(0, Math.min(arcLen, px)) / arcLen) * 100}%`;

    return {
      prevShown,
      selLabel,
      nextShown,
      prevStartOffset: pct(prevCenterPx),
      selStartOffset: '50%',
      nextStartOffset: pct(nextCenterPx),
    };
  }, [arcLen, gapPx, labels, nextIndex, prevIndex, selectedIndex]);

  const startRef = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const handlePointerDown: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (onForceShow) onForceShow();
    else setIsShown(true);
    startRef.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const handlePointerUp: React.PointerEventHandler<HTMLDivElement> = (e) => {
    const start = startRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    startRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    const travel = Math.hypot(dx, dy);
    if (travel < 24) return;
    const directionalScore = dx - dy;
    if (directionalScore > 16) return onChange(prevIndex);
    if (directionalScore < -16) return onChange(nextIndex);
  };
  const handlePointerCancel: React.PointerEventHandler<HTMLDivElement> = (e) => {
    if (startRef.current?.pointerId === e.pointerId) startRef.current = null;
  };

  const visible = typeof forcedShown === 'boolean' ? forcedShown : isShown;
  const baseBottom = 'calc(20px + env(safe-area-inset-bottom, 0px))';

  const wheelNode = (
    <motion.div
      className="fixed bottom-0 right-0 z-[60] lg:hidden touch-none select-none"
      style={{ bottom: baseBottom, touchAction: 'none' }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      initial={{ x: r - 120, y: r - 120, scale: 0.84, opacity: 1 }}
      animate={visible ? { x: 0, y: 20, scale: 1, opacity: 1 } : { x: r - 120, y: r - 100, scale: 0.84, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 520, damping: 34 }}
    >
      <div className="relative overflow-hidden" style={{ width: r, height: r }}>
        <div className="absolute" style={{ width: r * 2, height: r * 2, right: -r, bottom: -r }}>
          <div
            className={cn(
              'w-full h-full rounded-full',
              'bg-[#1C1C1E]/85 backdrop-blur-md',
              'border border-[#3A3A3C]/70 shadow-[0_14px_40px_rgba(0,0,0,0.55)]'
            )}
          />
        </div>

        <svg className="absolute inset-0" width={r} height={r} viewBox={`0 0 ${r} ${r}`} aria-hidden="true">
          <defs>
            <path id="public-compare-wheel-arc-all" d={arcPaths.all} />
            <path id="public-compare-wheel-arc-prev" d={arcPaths.prev} />
            <path id="public-compare-wheel-arc-selected" d={arcPaths.selected} />
            <path id="public-compare-wheel-arc-next" d={arcPaths.next} />
            <linearGradient id="public-compare-wheel-selected-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FF4D79" />
              <stop offset="55%" stopColor="#F33363" />
              <stop offset="100%" stopColor="#B71D49" />
            </linearGradient>
            <marker id="public-compare-wheel-arrow" viewBox="0 0 10 10" refX="8.5" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(142,142,147,0.55)" />
            </marker>
            <marker
              id="public-compare-wheel-arrow-rev"
              viewBox="0 0 10 10"
              refX="8.5"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" fill="rgba(142,142,147,0.55)" />
            </marker>
          </defs>

          <path d={arcPaths.prev} fill="none" stroke="transparent" strokeWidth={44} onClick={() => onChange(prevIndex)} style={{ cursor: 'pointer', pointerEvents: 'stroke' }} />
          <path d={arcPaths.selected} fill="none" stroke="transparent" strokeWidth={44} onClick={() => onChange(selectedIndex)} style={{ cursor: 'pointer', pointerEvents: 'stroke' }} />
          <path d={arcPaths.next} fill="none" stroke="transparent" strokeWidth={44} onClick={() => onChange(nextIndex)} style={{ cursor: 'pointer', pointerEvents: 'stroke' }} />

          <text fill="#8E8E93" fontSize="11.5" fontWeight="600" letterSpacing="0.06em" style={{ textTransform: 'uppercase', userSelect: 'none', cursor: 'pointer' }} onClick={() => onChange(prevIndex)}>
            <textPath href="#public-compare-wheel-arc-all" startOffset={arcTextLayout.prevStartOffset} textAnchor="middle">
              {arcTextLayout.prevShown}
            </textPath>
          </text>
          <text fill="url(#public-compare-wheel-selected-gradient)" fontSize="12.5" fontWeight="800" letterSpacing="0.08em" style={{ textTransform: 'uppercase', userSelect: 'none', cursor: 'pointer' }} onClick={() => onChange(selectedIndex)}>
            <textPath href="#public-compare-wheel-arc-all" startOffset={arcTextLayout.selStartOffset} textAnchor="middle">
              {arcTextLayout.selLabel}
            </textPath>
          </text>
          <text fill="#8E8E93" fontSize="11.5" fontWeight="600" letterSpacing="0.06em" style={{ textTransform: 'uppercase', userSelect: 'none', cursor: 'pointer' }} onClick={() => onChange(nextIndex)}>
            <textPath href="#public-compare-wheel-arc-all" startOffset={arcTextLayout.nextStartOffset} textAnchor="middle">
              {arcTextLayout.nextShown}
            </textPath>
          </text>

          <motion.path
            d={arcPaths.hintClockwise}
            fill="none"
            stroke="rgba(142,142,147,0.45)"
            strokeWidth={2}
            strokeLinecap="round"
            markerEnd="url(#public-compare-wheel-arrow)"
            initial={false}
            animate={{ opacity: visible ? 1 : 0 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
          />
          <motion.path
            d={arcPaths.hintAnti}
            fill="none"
            stroke="rgba(142,142,147,0.28)"
            strokeWidth={2}
            strokeLinecap="round"
            markerStart="url(#public-compare-wheel-arrow-rev)"
            initial={false}
            animate={{ opacity: visible ? 1 : 0 }}
            transition={{ duration: 0.24, ease: 'easeOut' }}
          />
        </svg>

        <div
          className="absolute left-1/2 bottom-3 -translate-x-1/2 text-[11px] font-semibold uppercase tracking-wide text-[#8E8E93] select-none pointer-events-none"
          style={{ textShadow: '0 1px 12px rgba(0,0,0,0.55)' }}
        >
          Swipe ↻ / ↺
        </div>
      </div>
    </motion.div>
  );

  if (typeof document === 'undefined') return wheelNode;
  return createPortal(wheelNode, document.body);
}

export const PublicComparePlansPage: React.FC = () => {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('annually');
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [expandedSection, setExpandedSection] = useState<string | null>(COMPARE_SECTIONS[0].id);
  const [wheelShown, setWheelShown] = useState(true);
  const publicScrollTopRef = useRef(0);
  const activeSection = COMPARE_SECTIONS[modIndex(selectedSectionIndex, COMPARE_SECTIONS.length)];

  const handlePublicScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
    const current = e.currentTarget.scrollTop;
    const delta = current - publicScrollTopRef.current;
    if (delta > 2) setWheelShown(false);
    else if (delta < 0) setWheelShown(true);
    publicScrollTopRef.current = current;
  };

  return (
    <PublicLayout>
      <div
        className="w-full h-screen bg-[#0B0B0D] text-white relative overflow-x-hidden overflow-y-auto overscroll-y-contain"
        data-scroll-container
        onScroll={handlePublicScroll}
      >
        {/* Subtle ambient gradient – matches Membership page */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[min(100%,720px)] h-96 bg-gradient-to-b from-[#F33363]/[0.05] via-transparent to-transparent rounded-full blur-3xl" />
        </div>

        <div className="relative w-full max-w-[90vw] md:max-w-[95vw] mx-auto md:px-4 py-12 md:py-16 lg:py-20 pb-12">
          {/* Header – premium typography & hierarchy */}
          <header className="mb-10 lg:mb-12">
            <p className="text-[#636366] text-xs font-semibold uppercase tracking-[0.2em] mb-4">
              Plan comparison
            </p>
            <div className="flex items-baseline gap-3 mb-4">
              <span className="rounded-2xl p-2 bg-[#1C1C1E]/80 border border-[#2C2C2E]">
                <GitCompare className="h-6 w-6 lg:h-7 lg:w-7 text-[#8E8E93]" strokeWidth={1.5} />
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white tracking-[-0.03em] leading-[1.1]">
                <span className="bg-clip-text text-transparent bg-gradient-to-b from-white via-white to-[#A1A1A6]">
                  Compare plans
                </span>
              </h1>
            </div>
            <div className="h-px w-16 bg-gradient-to-r from-[#F33363]/60 to-transparent rounded-full mb-5" />
            <p className="text-[#8E8E93] text-base lg:text-lg font-medium max-w-xl leading-relaxed">
              See how Free, Economy, Premium and Elite stack up. Pick the right fit for your career.
            </p>
          </header>

          {/* Billing Period Tabs + Back to plans – same row as MembershipPage */}
          <div className="mb-10 flex flex-wrap items-end justify-between gap-4 border-b border-[#3A3A3C] pb-0">
            <nav className="flex gap-6 lg:gap-8" aria-label="Billing period">
              <button
                type="button"
                onClick={() => setBillingPeriod('quarterly')}
                className={cn(
                  'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                  billingPeriod === 'quarterly'
                    ? 'text-white'
                    : 'text-[#8E8E93] hover:text-white'
                )}
                aria-current={billingPeriod === 'quarterly' ? 'page' : undefined}
              >
                Quarterly
                {billingPeriod === 'quarterly' && (
                  <motion.span
                    layoutId="public-compare-tab-underline"
                    className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                    transition={{ type: 'spring', duration: 0.3 }}
                  />
                )}
              </button>
              <button
                type="button"
                onClick={() => setBillingPeriod('annually')}
                className={cn(
                  'relative pb-3 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm',
                  billingPeriod === 'annually'
                    ? 'text-white'
                    : 'text-[#8E8E93] hover:text-white'
                )}
                aria-current={billingPeriod === 'annually' ? 'page' : undefined}
              >
                Annually
                {billingPeriod === 'annually' && (
                  <motion.span
                    layoutId="public-compare-tab-underline"
                    className="absolute left-0 right-0 bottom-0 h-0.5 bg-[#F33363] rounded-full"
                    transition={{ type: 'spring', duration: 0.3 }}
                  />
                )}
              </button>
            </nav>
            <Link
              to="/pricing"
              className="inline-flex items-center gap-1.5 py-2 px-4 border border-[#3A3A3C] rounded-br-none text-sm font-medium text-[#8E8E93] hover:text-[#F33363] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D] rounded-sm"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to plans
            </Link>
          </div>

          {/* Pricing card */}
          <motion.div
            key={billingPeriod}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
            className="rounded-2xl border border-[#3A3A3C] bg-[#1C1C1E] overflow-hidden mb-6"
          >
            <div className="grid grid-cols-4 gap-0 min-w-0">
              {COMPARE_PLANS.map((plan) => {
                const emi = COMPARE_EMI[billingPeriod][plan];
                return (
                  <div
                    key={plan}
                    className="p-4 border-b border-l first:border-l-0 border-[#3A3A3C]/50 text-center min-w-0"
                  >
                    <p className="text-xs lg:text-sm font-semibold text-[#8E8E93] uppercase tracking-wide">
                      {plan}
                    </p>
                    <p className="text-base lg:text-lg font-bold text-white mt-0.5">
                      {COMPARE_PRICES[billingPeriod][plan]}
                    </p>
                    {emi != null && (
                      <p className="text-xs text-[#8E8E93] mt-0.5 font-medium">EMI from ₹{emi.toLocaleString()}/mo</p>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Mobile: single active section */}
          <motion.div
            key={`${billingPeriod}-${activeSection.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.18 }}
            className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden lg:hidden"
          >
            <div className="px-4 py-4 border-b border-[#3A3A3C]/50">
              <div className="flex items-center justify-between gap-3">
                <span className="text-base font-semibold text-white">{activeSection.title}</span>
                <span className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wide">Swipe / tap selector</span>
              </div>
            </div>
            <div className="overflow-x-auto">
              <div
                className={cn(
                  'grid grid-cols-[minmax(180px,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-0 min-w-[500px]',
                  'border-b border-[#3A3A3C]/50 bg-[#252527]/50'
                )}
              >
                <div className="py-3 px-4 text-xs font-semibold text-[#8E8E93] uppercase tracking-wide text-left">
                  Benefit
                </div>
                {COMPARE_PLANS.map((plan) => (
                  <div key={plan} className="py-3 px-2 text-xs font-semibold text-[#8E8E93] uppercase tracking-wide text-center min-w-0">
                    {plan}
                  </div>
                ))}
              </div>
              {activeSection.rows.map((row, idx) => (
                <div
                  key={idx}
                  className={cn(
                    'grid grid-cols-[minmax(180px,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-0 min-w-[500px] items-center min-h-[48px]',
                    'border-b border-[#3A3A3C]/30 last:border-b-0',
                    idx % 2 === 1 && 'bg-[#252527]/30'
                  )}
                >
                  <div className="py-2.5 px-4 text-sm text-[#E5E5EA] font-medium text-left">
                    {row.label}
                  </div>
                  {COMPARE_PLANS.map((plan) => (
                    <div key={plan} className="py-2.5 px-2 flex justify-center items-center min-w-0">
                      <Cell value={getCompareCell(row, plan, billingPeriod)} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Desktop: original accordion */}
          <div className="hidden lg:block space-y-2">
            {COMPARE_SECTIONS.map((section) => {
              const isExpanded = expandedSection === section.id;
              return (
                <motion.div
                  key={section.id}
                  initial={false}
                  className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => setExpandedSection(isExpanded ? null : section.id)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-4 lg:px-5 lg:py-4 min-h-[56px] hover:bg-[#252527] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-inset"
                  >
                    <span className="text-base font-semibold text-white">{section.title}</span>
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-[#8E8E93] shrink-0" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-[#8E8E93] shrink-0" />
                    )}
                  </button>
                  <AnimatePresence initial={false}>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-[#3A3A3C]/50 overflow-x-auto">
                          <div
                            className={cn(
                              'grid grid-cols-[minmax(180px,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-0 min-w-[500px]',
                              'border-b border-[#3A3A3C]/50 bg-[#252527]/50'
                            )}
                          >
                            <div className="py-3 px-4 text-xs font-semibold text-[#8E8E93] uppercase tracking-wide text-left">
                              Benefit
                            </div>
                            {COMPARE_PLANS.map((plan) => (
                              <div
                                key={plan}
                                className="py-3 px-2 text-xs font-semibold text-[#8E8E93] uppercase tracking-wide text-center min-w-0"
                              >
                                {plan}
                              </div>
                            ))}
                          </div>
                          {section.rows.map((row, idx) => (
                            <div
                              key={idx}
                              className={cn(
                                'grid grid-cols-[minmax(180px,1.6fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] gap-0 min-w-[500px] items-center min-h-[48px]',
                                'border-b border-[#3A3A3C]/30 last:border-b-0',
                                idx % 2 === 1 && 'bg-[#252527]/30'
                              )}
                            >
                              <div className="py-2.5 px-4 text-sm text-[#E5E5EA] font-medium text-left">
                                {row.label}
                              </div>
                              {COMPARE_PLANS.map((plan) => (
                                <div
                                  key={plan}
                                  className="py-2.5 px-2 flex justify-center items-center min-w-0"
                                >
                                  <Cell value={getCompareCell(row, plan, billingPeriod)} />
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* CTAs – public routes only; no protected routes */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">

            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 min-h-[48px] px-8 rounded-2xl bg-[#F55C8D] hover:bg-[#F33363] text-white font-bold transition-colors"
            >

              Get Started
            </Link>
          </div>
        </div>

        <QuarterCircleSectionPicker
          labels={COMPARE_SECTIONS.map((s) => s.title)}
          selectedIndex={modIndex(selectedSectionIndex, COMPARE_SECTIONS.length)}
          onChange={(next) => setSelectedSectionIndex(modIndex(next, COMPARE_SECTIONS.length))}
          forcedShown={wheelShown}
          onForceShow={() => setWheelShown(true)}
        />
      </div>
    </PublicLayout>
  );
};
